import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { SCREEN_EDGE_GAP } from '@constants/layout';
import { COMMON } from '@locales/id/messages';
import { useDismiss } from '@lib/hooks';
import { Icon } from './Icon';
import { ExitGhost } from './useExitGhost';
import type { IconName } from './icons';

export interface MenuAction {
  key: string;
  label: string;
  icon: IconName;
  onSelect: () => void;
  /** destructive: red, and set apart by a line above it */
  danger?: boolean;
  /** why it cannot be chosen now: shown under the label, the item stays in the list but does nothing */
  disabled?: string;
}

/** Gap between the button and its list (px). */
const OFFSET = 4;

/**
 * A "⋯" button with a short list of actions (WAI-ARIA menu button): Enter, Space or ↓ opens it on the first action,
 * ↑ on the last; ↑/↓ (wrapping), Home/End move; Esc or a press outside closes it and returns the focus to the button.
 * The list is fixed to the screen (a table's scrolling wrapper never clips it), under the button or above it when
 * there is no room, and follows the button while the page scrolls (it closes once the button is out of sight). `busy`: an action is running (a spinner, nothing to press).
 * Choosing an action closes it first, so a dialog it opens gets the focus. Renders nothing without actions.
 */
export function ActionMenu({ label, actions, disabled, busy }: { label: string; actions: MenuAction[]; disabled?: boolean; busy?: boolean }) {
  const [open, setOpen] = useState<'first' | 'last' | null>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = useCallback((refocus: boolean) => {
    setOpen(null);
    setPlace(null);
    if (refocus) trigger.current?.focus();
  }, []);
  useDismiss(wrap, open !== null, close);

  // placed under its button (above it when there is no room), and kept there while the page scrolls or resizes
  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    const measure = () => {
      if (!list.current || !trigger.current) return;
      const t = trigger.current.getBoundingClientRect();
      // the button scrolled out of sight: the list has nothing to hang from
      if (t.bottom < 0 || t.top > window.innerHeight) return close(false);
      const h = list.current.offsetHeight;
      const w = list.current.offsetWidth;
      const up = window.innerHeight - t.bottom - SCREEN_EDGE_GAP < h && t.top - SCREEN_EDGE_GAP >= h;
      const left = Math.max(SCREEN_EDGE_GAP, Math.min(t.right - w, document.documentElement.clientWidth - SCREEN_EDGE_GAP - w));
      setPlace({ '--pop-origin': up ? 'bottom right' : 'top right', left, top: up ? t.top - h - OFFSET : t.bottom + OFFSET } as CSSProperties);
    };
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', later, { capture: true, passive: true });
    window.addEventListener('resize', later);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', later, { capture: true });
      window.removeEventListener('resize', later);
    };
  }, [open, close]);
  const placed = place !== null;
  useEffect(() => {
    if (!placed) return;
    const items = list.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    items?.[open === 'last' ? items.length - 1 : 0]?.focus({ preventScroll: true });
  }, [placed]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!actions.length) return null;

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const to = e.key === 'ArrowDown' ? (i + 1) % items.length : e.key === 'ArrowUp' ? (i - 1 + items.length) % items.length : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1;
    if (to >= 0) {
      e.preventDefault();
      items[to]?.focus();
    } else if (e.key === 'Tab') close(false);
  };
  const pick = (a: MenuAction) => {
    if (a.disabled) return;
    close(true);
    a.onSelect();
  };
  return (
    <div className="action-menu" ref={wrap} onClick={(e) => e.stopPropagation()}>
      <button
        ref={trigger}
        type="button"
        className="action-menu-trigger"
        aria-label={busy ? `${label} (${COMMON.processing})` : label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open !== null}
        aria-controls={open ? menuId : undefined}
        aria-busy={busy || undefined}
        disabled={disabled || busy}
        onClick={() => (open ? close(false) : setOpen('first'))}
        onKeyDown={(e) => {
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
            e.preventDefault();
            setOpen(e.key === 'ArrowUp' ? 'last' : 'first');
          }
        }}
      >
        {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="more" size={20} />}
      </button>
      {open && <ExitGhost of={list} kind="pop" />}
      {open && (
        <div id={menuId} ref={list} role="menu" aria-label={label} className="menu action-menu-list" style={place ?? { visibility: 'hidden' }} onKeyDown={onKey}>
          {actions.map((a, i) => [
            a.danger && i > 0 && !actions[i - 1].danger ? <hr key={`${a.key}-sep`} className="menu-sep" role="separator" /> : null,
            <button
              key={a.key}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={`menu-item ${a.danger ? 'menu-danger' : ''} ${a.disabled ? 'is-disabled' : ''}`}
              aria-disabled={a.disabled ? true : undefined}
              // the label stays the name; the reason is its description
              aria-label={a.disabled ? a.label : undefined}
              aria-describedby={a.disabled ? `${menuId}-${a.key}-why` : undefined}
              onClick={() => pick(a)}
            >
              <Icon name={a.icon} />
              <span className="menu-item-grow menu-item-text">
                {a.label}
                {a.disabled && (
                  <span className="menu-item-hint" id={`${menuId}-${a.key}-why`}>
                    {a.disabled}
                  </span>
                )}
              </span>
            </button>,
          ])}
        </div>
      )}
    </div>
  );
}
